import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import { showToast } from './toastSlice'
import {
  addCashbackRuleUrl,
  deleteCashbackRuleUrl,
  editCashbackRuleUrl,
  fetchCashbackRulesUrl,
  fetchDistrictsUrl,
  fetchProductsUrl,
  fetchStatesUrl,
} from 'src/services/urls'
const { createSlice, createAsyncThunk } = require('@reduxjs/toolkit')

const intitialState = {
  states: [],
  districts: [],
  products: [],
  loading: false,
  pageSize: 10,
  error: '',
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  sortBy: 'productName',
  orderBy: 'asc',
  filterBy: '',
  activePage: 0,
}

export const fetchProductsAndStates = createAsyncThunk(
  'cashbackRule/fetchProductsAndStates',
  async (_, { dispatch }) => {
    try {
      const productsResponse = await makeRequest(fetchProductsUrl())
      const statesRepsonse = await makeRequest(fetchStatesUrl())
      const districtResponse = await makeRequest(fetchDistrictsUrl())
      return {
        products: productsResponse?.data?.data,
        states: statesRepsonse?.data?.data,
        districts: districtResponse?.data?.data,
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const editCashbackRule = createAsyncThunk(
  'cashbackRule/editCashbackRule',
  async (data, { dispatch }) => {
    try {
      let body = {
        ruleStartTimestamp: data?.ruleStartTimestamp,
        ruleEndTimestamp: data?.ruleEndTimestamp,
      }

      if (data?.isActive !== undefined) body = { isActive: data?.isActive }
      const response = await makeRequest(editCashbackRuleUrl(data?._id), 'PATCH', body)
      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        dispatch(fetchCashbackRules(data?.filters))
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const addCashbackRule = createAsyncThunk(
  'cashbackRule/createCashbackRule',
  async (data, { dispatch }) => {
    try {
      const body = { ...data }
      delete body?._id
      delete body?.filters
      const response = await makeRequest(addCashbackRuleUrl(), 'POST', body)
      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
        dispatch(fetchCashbackRules(data?.filters))
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const deleteCashbackRule = createAsyncThunk(
  'cashbackRule/deleteCashbackRule',
  async (data, { dispatch }) => {
    try {
      const response = await makeRequest(deleteCashbackRuleUrl(data?.id), 'DELETE')
      const { displayMessage, status } = response.data
      if (response.status === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        dispatch(fetchCashbackRules(data?.filters))
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchCashbackRules = createAsyncThunk(
  'cashbackRule/fetchCashbackRules',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const sortBy = options?.sortBy
      const orderBy = options?.orderBy
      let url = fetchCashbackRulesUrl(skip, limit, sortBy, orderBy)
      if (options?.productName) {
        url += `&productName=${options.productName}`
      }
      if (options?.regionType) {
        url += `&regionType=${options.regionType}`
      }
      if (options?.ruleType) {
        url += `&ruleType=${options.ruleType}`
      }
      if (options?.regionValues) {
        let ids = []
        options.regionValues.map((val) => ids.push(val?.value))
        url += `&regionValue=${ids}`
      }

      const response = await makeRequest(url)

      let docs = []
      if (response.data) {
        docs = response.data.data.docs.map((item, i) => {
          const productDetails = item.product.map((p) => ({
            productName: p.productName,
            gtin: `${p.gtin}`,
            ratio: `${p.ratio}`,
          }))
          return { ...item, srno: i + 1, productDetails: productDetails }
        })
      }

      return { docs, data: response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const cashbackRuleSlice = createSlice({
  name: 'cashbackRule',
  initialState: intitialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload
    },
    setPageSize(state, action) {
      state.pageSize = action.payload
    },
    setSortBy(state, action) {
      state.sortBy = action.payload
    },
    setOrderBy(state, action) {
      state.orderBy = action.payload
    },
    setFilterBy(state, action) {
      state.filterBy = action.payload
    },
    setActivePage(state, action) {
      state.activePage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCashbackRules.pending, (state, action) => {
      state.loading = true
    })
    builder.addCase(fetchCashbackRules.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
      if (!action.payload.error) {
        state.data = action.payload.data
        state.docs = action.payload.docs
      }
    })
    builder.addCase(fetchCashbackRules.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
      state.data = {}
      state.docs = []
    })
    builder.addCase(fetchProductsAndStates.fulfilled, (state, action) => {
      state.products = action.payload?.products
      state.states = action.payload?.states
      state.districts = action.payload?.districts
    })
    builder.addCase(addCashbackRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addCashbackRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editCashbackRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editCashbackRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(deleteCashbackRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(deleteCashbackRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
  },
})

export default cashbackRuleSlice.reducer
export const { setLoading, setPageSize, setSortBy, setFilterBy, setOrderBy, setActivePage } =
  cashbackRuleSlice.actions
